'use client';
import SearchAutocomplete from './_SearchAutocomplete';
import { prependSlash } from '@repo/ui/src/components/Anchor/helpers';
import SearchIcon from '@repo/ui/src/components/Icons/SearchIcon';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { classNames } from '@repo/ui/src/utils/classNames';
import React, { useEffect } from 'react';

export interface SearchProps {
  link?: string;
  placeholder?: string;
  searchCategory?: SearchCategory;
}

export enum SearchCategory {
  all = 'None',
  products = 'Product',
  recipes = 'Recipe',
  learn = 'Learn',
  support = 'Support',
}

const getSearchTerm = (form: HTMLFormElement) => new FormData(form).get('searchTerm') as string;

const Search = ({
  link = 'search',
  placeholder = 'Search',
  searchCategory = SearchCategory.all,
}: SearchProps) => {
  const { push } = useCurrentUrl();
  const ref = React.useRef<HTMLInputElement>(null);
  const autocompleteRef = React.useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    const { currentTarget } = e;
    setIsOpen((curr) => {
      if (curr) {
        handleSearch(getSearchTerm(currentTarget.form!));
        return curr;
      }
      return !curr;
    });
  };

  useEffect(() => {
    if (isOpen) {
      ref.current?.focus();
    }
  }, [isOpen]);

  const handleSearch = async (term: string) => {
    const queryParams = new URLSearchParams({
      term,
    });
    if (searchCategory !== SearchCategory.all) {
      queryParams.set('type', searchCategory);
    }
    const url = `${prependSlash(link)}?${queryParams}`;
    setIsOpen(false);
    ref.current!.value = term;
    await push(url);

    setSearchTerm('');
    ref.current!.value = '';
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        return handleSearch(getSearchTerm(e.currentTarget));
      }}
      className="tw-relative tw-hidden lg:tw-grid tw-items-center"
    >
      <input
        type="text"
        name="searchTerm"
        placeholder={placeholder}
        autoComplete="off"
        aria-autocomplete="list"
        aria-controls="global-nav-autocomplete"
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.currentTarget.blur();
          }

          if (e.key === 'ArrowDown') {
            e.preventDefault();
            autocompleteRef.current?.focus();
          }
        }}
        onChange={(e) => setSearchTerm(e.currentTarget.value)}
        onBlur={(e) => {
          const { currentTarget, relatedTarget } = e;
          if (!currentTarget.form?.contains(relatedTarget)) {
            setTimeout(() => {
              setIsOpen(false);
            }, 100);
          }
        }}
        className={classNames(
          'tw-rounded-[80px] tw-border tw-border-neutral-300 tw-bg-transparent tw-py-2 tw-px-8 tra-text-web-body-l tw-text-white focus:tw-outline-brand-200 focus:tw-outline-2 focus-visible:tw-outline',
          isOpen
            ? 'tw-w-[15vw] tw-opacity-1 [transition:opacity_200ms,width_250ms]'
            : 'tw-w-0 tw-opacity-0 [transition:opacity_200ms_100ms,width_250ms_100ms] tw-pointer-events-none',
        )}
        ref={ref}
      />
      <button
        aria-label={placeholder}
        type="button"
        className="tw-w-6 tw-text-white tw-absolute tw-right-5 tw-inset-y-2 tw-cursor-pointer"
        onClick={handleOpen}
        onBlur={(e) => {
          const { currentTarget, relatedTarget } = e;
          if (!currentTarget.form?.contains(relatedTarget)) {
            setIsOpen(false);
          }
        }}
      >
        <SearchIcon />
      </button>
      {searchCategory === SearchCategory.all && searchTerm && (
        <SearchAutocomplete
          searchTerm={searchTerm}
          search={handleSearch}
          className={classNames(
            'tw-duration-150',
            isOpen
              ? 'tw-opacity-1 tw-delay-300'
              : 'tw-opacity-0 tw-pointer-events-none tw-translate-y-3',
          )}
          id="global-nav-autocomplete"
          ref={autocompleteRef}
          returnFocus={() => {
            ref.current?.focus();
          }}
        />
      )}
    </form>
  );
};

export default Search;
